import Action from '~/editor/history/action'
import { ActionType } from '~/editor/history/action.interfaces'

interface FilterState {
  index: number
}

export default class History {
  index: number
  actions: Action[]
  filterStates: Map<string, FilterState>

  constructor() {
    this.index = -1
    this.actions = []
    this.filterStates = new Map()
  }

  /**
   * Undo the last action.
   * If allowedActions is provided, uses a separate filtered index.
   */
  undo(allowedActions?: ActionType[]) {
    if (allowedActions && allowedActions.length > 0) {
      const key = this.getFilterKey(allowedActions)
      const filtered = this.actions.filter(a => allowedActions.includes(a.name))

      // Initialize state if not present
      if (!this.filterStates.has(key)) {
        this.filterStates.set(key, { index: filtered.length - 1 })
      }
      const state = this.filterStates.get(key)!

      if (state.index >= 0 && state.index < filtered.length) {
        console.log('UNDOING', filtered[state.index])
        filtered[state.index].undo()
        state.index--
      }
    } else {
      // Default undo using main history
      if (this.index >= 0 && this.index < this.actions.length) {
        console.log('UNDOING', this.actions[this.index])
        this.actions[this.index].undo()
        this.index--
      }
    }
  }

  /**
   * Redo the next action.
   * If allowedActions is provided, uses a separate filtered index.
   */
  redo(allowedActions?: ActionType[]) {
    if (allowedActions && allowedActions.length > 0) {
      const key = this.getFilterKey(allowedActions)
      const filtered = this.actions.filter(a => allowedActions.includes(a.name))

      // Initialize state if not present
      if (!this.filterStates.has(key)) {
        this.filterStates.set(key, { index: -1 })
      }
      const state = this.filterStates.get(key)!

      if (state.index + 1 < filtered.length) {
        state.index++
        console.log('REDOING', filtered[state.index])
        filtered[state.index].redo()
      }
    } else {
      // Default redo using main history
      if (this.index + 1 < this.actions.length) {
        this.index++
        console.log('REDOING', this.actions[this.index])
        this.actions[this.index].redo()
      }
    }
  }

  /**
   * Append a new action, pruning future actions.
   */
  append(action: Action) {
    // Prune future actions in main history
    if (this.index < this.actions.length - 1) {
      this.actions = this.actions.slice(0, this.index + 1)
    }

    // Add action
    this.index++
    this.actions.push(action)

    // Reset filter states so they reinitialize on next filtered undo/redo
    this.filterStates.clear()
  }

  /**
   * Clear all history.
   */
  clear() {
    this.index = -1
    this.actions = []
    this.filterStates.clear()
  }

  /**
   * Generate a stable key for a given allowedActions array.
   */
  getFilterKey(allowed: ActionType[]): string {
    // Sort to ensure consistent key regardless of order
    return allowed.slice().sort().join('|')
  }
}
